import { useEffect } from 'react';
import { faColumns3 as faColumns3Light } from '@fortawesome/pro-light-svg-icons';
import { faColumns3, faGears } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import type { IAppPreviewTab } from '~/components/organisms/AppPreview/AppPreviewState';
import { UpdateTabsSettingsForm } from '~/components/forms/TabsSettingsForm';
import { Breadcrumbs } from '~/components/molecules/Breadcrumbs';
import { Heading } from '~/components/molecules/Heading';
import { useAppPreviewDispatch } from '~/components/organisms/AppPreview';
import { AppBreadcrumbs } from '~/components/organisms/Breadcrumbs';
import { useNavigate } from '~/components/utils/Navigation';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';

export type IAppTabsSettingsPageProps = {
  appId: string;
  nextUrl?: string;
  cancelUrl?: string;
};

export const AppTabsSettingsPage: React.FC<IAppTabsSettingsPageProps> = (
  props,
) => {
  const { appId, nextUrl, cancelUrl } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appQuery = useAppQuery({ appId });
  const appPreviewDispatch = useAppPreviewDispatch();

  const parentUrl = `/apps/${appId}/settings`;

  useEffect(() => {
    const startTab = (appQuery.data?.settings?.press?.env?.startTab ??
      'today') as IAppPreviewTab;
    appPreviewDispatch.navigateToRoute({
      appId,
      path: '/',
      tab: startTab,
    });
  }, [appPreviewDispatch, appId, appQuery.data]);

  return (
    <>
      <Helmet>
        <title>{t('AppTabsSettingsPage.seo.title')}</title>
      </Helmet>

      <AppBreadcrumbs appId={appId}>
        <Breadcrumbs.Action href={parentUrl}>
          <FontAwesomeIcon icon={faGears} />
          {t('common.settings')}
        </Breadcrumbs.Action>
        <Breadcrumbs.Text current>
          <FontAwesomeIcon icon={faColumns3} />
          {t('AppTabsSettingsPage.title')}
        </Breadcrumbs.Text>
      </AppBreadcrumbs>

      <Heading
        icon={<FontAwesomeIcon icon={faColumns3Light} />}
        title={t('AppTabsSettingsPage.title')}
        subtitle={t('AppTabsSettingsPage.subtitle')}
        previousUrl={parentUrl}
      />

      <UpdateTabsSettingsForm
        appId={appId}
        onSuccess={nextUrl ? () => navigate(nextUrl) : undefined}
        cancelUrl={cancelUrl ?? parentUrl}
      />
    </>
  );
};
